import React, { useCallback, useEffect, useRef } from 'react'
import { track } from 'analytics'
import type { ModalComponentProps } from 'modals'
import links from 'links'
import { useIntl } from 'intl'
import { useAb, useFt } from 'hooks'
import { constants } from 'helpers'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

import { useUser } from 'modules/user'
import { useCart, CartContextProvider, useCartModify } from 'modules/cart'
import { PaymentFlowProvider, InquireProvider, RecurlyProvider, useIsApplePayAvailable } from 'modules/payment'

import type { ProductGroup } from 'typings/graphql'
import { Bone } from 'components/layout'
import { ModalButtons, ModalHeadline, ModalWithLayers, useModalWithLayers } from 'components/feedback'
import { buttonMessages } from 'components/inputs'
import ApplePayCartButton from 'compositions/payment/ExpressCheckout/components/ApplePayButton/ApplePayCartButton'
import useCartPaymentFlow from 'compositions/modals/CartModal/util/useCartPaymentFlow'
import { FirstProductModalView } from 'compositions/modals/FirstProductModal/FirstProductModal'

import { useOrderBill } from 'compositions/orderItems'
import CartItems from './components/CartItems/CartItems'
import CartBill from './components/CartBill/CartBill'
import GiftWithSpendThresholdProgress from './components/GiftWithSpendThresholdProgress/GiftWithSpendThresholdProgress'
import ShippingStatus from './components/ShippingStatus/ShippingStatus'

import BundlingBanner from './components/BundlingBanner/BundlingBanner'
import messages from './messages'


const isFullBottle = (volume: string, unit: string) => {
  try {
    const parsedVolume = parseFloat(volume)

    if (unit === 'ml') {
      return parsedVolume > 8
    }

    if (unit === 'oz') {
      return parsedVolume > 0.27
    }
  }
  catch (error) {}

  return false
}

const CartModalView: React.FC<CartModule.CartModalProps> = ({ flow, applePayButton }) => {
  const intl = useIntl()
  const cartContext = useCart()
  const cartModifyContext = useCartModify()
  const { cart, isFetching } = cartContext

  const isGwpSpendThresholdEnabled = useFt(constants.features.gwpSpendThreshold)
  const isBuy5Pay4Enabled = useFeatureIsOn(constants.features.buy5pay4)

  const { discounts, shipping, credits, subTotal, total } = cart || {}
  const bill = useOrderBill({ discounts, shipping, credits, subTotal, total })

  // fire only once after open and fetching
  const isCartDisplayFiredRef = useRef(false)

  const isEmpty = !cart || cart.isEmpty

  useEffect(() => {
    if (!cart || isCartDisplayFiredRef.current) {
      return
    }

    isCartDisplayFiredRef.current = true

    const productType = new Set<Analytics.ProductType>()
    // TODO remove later, when we collect data in productType - added on 2023-10-10 by maddoger
    const ecommerceType = new Set<Analytics.CartEcommerceType>()

    if (cart.hasSubscription) {
      productType.add('Perfume subscription')
    }

    if (cart.hasGiftSubscription) {
      productType.add('Gift subscription')
    }

    if (cart.hasGiftCard) {
      productType.add('Gift card')
    }

    if (cart.hasQueueItems) {
      productType.add('Buy queue')
      ecommerceType.add('Buy queue')
    }

    if (cart.hasLimitedDrops) {
      productType.add('Limited drop')
    }

    if (cart.hasProducts) {
      cart.products.forEach(({ product, offer, price }) => {
        const { volume, unit } = product
        const typeGroup = product.typeGroup as ProductGroup

        if (offer === 'TOSSIN' || offer === 'TOSSIN_EMAIL') {
          productType.add('Toss-in')
          ecommerceType.add('Toss-in')
        }
        // we don't count other "gift with purchase" offers like JASON_WU or SAKS,
        // GIFT_SUBSCRIPTION is in separate field of "cart". Usually they come Free, so exclude them
        else if (price === 0) {
          return
        }
        else if (typeGroup === 'PerfumeCase' || typeGroup === 'FragranceStorageBox') {
          productType.add('Ecommerce')
          ecommerceType.add('Extras')
        }
        else if (typeGroup === 'FlashSale') {
          productType.add('Private sale')
          ecommerceType.add('Flash sale')
        }
        else if (typeGroup === 'AcademyCourse') {
          productType.add('Scentbird Academy')
        }
        // I didn't find other way to split full bottles from 0.27 oz vials, they all "Perfume"
        else if (typeGroup === 'Perfume' && isFullBottle(volume, unit)) {
          productType.add('Ecommerce')
          ecommerceType.add('Full-size bottles')
        }
        else {
          productType.add('Ecommerce')
          ecommerceType.add('A la carte')
        }
      })
    }

    track('Cart display', {
      flow,
      productType: Array.from(productType).sort(),
      ecommerceType: Array.from(ecommerceType).sort(),
    })
  }, [ isEmpty, flow, cart ])

  const title = isEmpty
    ? messages.empty.title
    : { ...messages.default.title, values: { total: intl.formatPrice(cart.total) } }

  const text = isEmpty
    ? messages.empty.text
    : null

  return (
    <CartContextProvider value={{ cart: cartContext, modifyCart: cartModifyContext }}>
      <ModalHeadline title={title} text={text} />
      <div className="flex flex-1 flex-col items-stretch justify-start">
        {
          !cart ? (
            <Bone pw={100} h={100} />
          ) : (
            <>
              {
                !isEmpty && isGwpSpendThresholdEnabled && (
                  <GiftWithSpendThresholdProgress className="mb-32" />
                )
              }
              {
                !isEmpty && isBuy5Pay4Enabled && (
                  <BundlingBanner className="mb-32" />
                )
              }
              <CartItems cart={cart} />
              {
                !isEmpty && (
                  <CartBill className="mt-32" bill={bill} />
                )
              }
            </>
          )
        }
      </div>
      <ModalButtons
        primary={{
          title: buttonMessages.checkout,
          to: links.checkout,
          disabled: isEmpty || isFetching,
          loading: isFetching,
          fullWidth: true,
        }}
      />
      {applePayButton}
      {
        !isEmpty && (
          <ShippingStatus className="mt-16" cart={cart} />
        )
      }
    </CartContextProvider>
  )
}

const FirstProductModalLayer = () => {
  const { activeLayers, closeLayer } = useModalWithLayers()

  const layerId = 'firstProduct'

  const handleClose = useCallback(() => {
    closeLayer(layerId)
  }, [ closeLayer, layerId ])

  const layerProps = activeLayers?.get(layerId) || {}

  return (
    <ModalWithLayers.Layer id={layerId}>
      <FirstProductModalView
        {...layerProps}
        closeModal={handleClose}
      />
    </ModalWithLayers.Layer>
  )
}

const CartModalProvidersWithApple = ({ flow }) => {
  const { cartPaymentFlow, inquire } = useCartPaymentFlow({ purchaseLevel: 'ALL' })

  const applePayButton = (
    <ApplePayCartButton className="mt-16" />
  )

  return (
    <RecurlyProvider>
      <PaymentFlowProvider value={cartPaymentFlow}>
        <InquireProvider value={inquire}>
          <CartModalView flow={flow} applePayButton={applePayButton} />
        </InquireProvider>
      </PaymentFlowProvider>
    </RecurlyProvider>
  )
}

const CartModal: React.FC<ModalComponentProps & CartModule.CartModalProps> = ({ name, flow, closeModal }) => {
  const isApplePayAvailable = useIsApplePayAvailable()
  const { isLoggedIn } = useUser()
  const isApplePayInCartEnabled = useAb(constants.abTests.applePayInCart) === 'b'

  const isApplePayVisible = isApplePayInCartEnabled && isLoggedIn && isApplePayAvailable

  return (
    <ModalWithLayers
      type="sidebar-right"
      underMobileHeader
      closeModal={closeModal}
      data-testid={name}
    >
      {
        isApplePayVisible ? (
          <CartModalProvidersWithApple flow={flow} />
        ) : (
          <CartModalView flow={flow} />
        )
      }
      <FirstProductModalLayer />
    </ModalWithLayers>
  )
}


export default CartModal
